import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { namespace } from '@common-config/constants';
import { backendApi, type MyBridgeAgentConversationRow } from '@common/reducers/backend-api/backend-api';
import { PauseCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { pausedBadge, pausedConversations, resumeChats } from './paused-chats';
import { useBridgeChatNames } from './useBridgeChatNames';

/**
 * FE#484 — paused (takeover) chats, surfaced where the owner looks: a badge on the
 * agent row with a one-click "Resume agent" per chat, and an offer in the agent
 * form. Both reuse the Conversations takeover toggle (PATCH status:false); the
 * pause semantics are unchanged.
 */

/** One page is enough to count + list (the Conversations page caps at 200 too). */
const PAUSED_PAGE = 200;
const TAKEOVER_AMBER = '#d97706';
const mutedStyle = { color: 'var(--color-text-muted)' } as const;

/** The paused chats of one agent, and the actions that release them. */
export function usePausedChats(agentId: string | undefined, enabled = true) {
  const { t } = useTranslation(namespace);
  const { data, isLoading, isError } = backendApi.useGetMyBridgeAgentTakeoverConversationsQuery(
    { agentId: agentId ?? '', limit: PAUSED_PAGE },
    { skip: !enabled || !agentId },
  );
  const [patch] = backendApi.useSetMyBridgeConversationTakeoverMutation();
  const [busyChatId, setBusyChatId] = useState<string | null>(null);
  const rows = pausedConversations(data?.conversations);
  const release = (chatId: string) => patch({ agentId: agentId ?? '', chatId, status: false }).unwrap();

  /** Resume one chat (the popover button). */
  const resumeOne = async (chatId: string) => {
    setBusyChatId(chatId);
    try {
      await release(chatId);
      toast.success(t('myBridge.paused.hint'));
    } catch {
      toast.error(t('myBridge.paused.error'));
    } finally {
      setBusyChatId(null);
    }
  };

  /** Resume every paused chat (the form offer); returns the chats that failed. */
  const resumeAll = async () => {
    const { failed } = await resumeChats(rows.map((r) => r.chatId), release);
    if (failed.length) toast.error(t('myBridge.paused.error'));
    else if (rows.length) toast.success(t('myBridge.paused.hint'));
    return failed;
  };

  return { isLoading, isError, conversations: data?.conversations, hasMore: data?.hasMore, rows, busyChatId, resumeOne, resumeAll };
}

/** The paused chats with a "Resume agent" button each, and what release means. */
export const PausedChatsList: React.FC<{
  rows: MyBridgeAgentConversationRow[];
  busyChatId: string | null;
  onResume: (chatId: string) => void;
}> = ({ rows, busyChatId, onResume }) => {
  const { t } = useTranslation(namespace);
  const { nameOf } = useBridgeChatNames();
  return (
    <div className="space-y-2" data-testid="paused-chats-list">
      <p className="text-xs" style={mutedStyle}>{t('myBridge.paused.hint')}</p>
      <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
        {rows.map((row) => (
          <li key={row.conversationId} className="flex items-center gap-2 py-1.5">
            <span className="flex-1 min-w-0 truncate text-sm" dir="auto">{nameOf(row.chatId)}</span>
            <Button
              size="sm"
              variant="outline"
              disabled={busyChatId === row.chatId}
              onClick={() => onResume(row.chatId)}
              aria-label={t('myBridge.paused.resumeAria', { name: nameOf(row.chatId) })}
              data-testid="paused-chat-resume"
            >
              {t('myBridge.paused.resume')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** "Paused chats: N" on the agent row; opens the list. Renders nothing when none are paused. */
export const PausedChatsBadge: React.FC<{ agentId: string }> = ({ agentId }) => {
  const { t } = useTranslation(namespace);
  const { conversations, hasMore, rows, busyChatId, resumeOne } = usePausedChats(agentId);
  const badge = pausedBadge(conversations, hasMore);
  if (!badge) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ backgroundColor: 'rgba(245,158,11,0.15)', color: TAKEOVER_AMBER }}
          data-testid="paused-chats-badge"
        >
          <PauseCircle className="h-3 w-3" />
          {badge.more
            ? t('myBridge.paused.badgeMore', { count: badge.count })
            : t('myBridge.paused.badge', { count: badge.count })}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <PausedChatsList rows={rows} busyChatId={busyChatId} onResume={(id) => void resumeOne(id)} />
      </PopoverContent>
    </Popover>
  );
};

/** In the agent form: "Also resume the N paused chats" when auto-takeover is switched off. */
export const ResumePausedOffer: React.FC<{ count: number; checked: boolean; onChange: (checked: boolean) => void }> = ({ count, checked, onChange }) => {
  const { t } = useTranslation(namespace);
  if (count <= 0) return null;
  return (
    <div className="space-y-0.5" data-testid="resume-paused-offer">
      <label className="flex items-center gap-2 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={(v) => onChange(v === true)} data-testid="resume-paused-checkbox" />
        <span className="text-sm font-medium text-ai-text">{t('myBridge.paused.alsoResume', { count })}</span>
      </label>
      <p className="text-xs" style={mutedStyle}>{t('myBridge.paused.hint')}</p>
    </div>
  );
};

/**
 * The form's offer, wired to the agent's paused chats. The agent form hands in a ref
 * that this field keeps pointing at "resume them all" (null while nothing is paused),
 * so the form's save can run it without owning the query.
 */
export const PausedOfferField: React.FC<{
  agentId: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  resumeRef: React.MutableRefObject<(() => Promise<unknown>) | null>;
}> = ({ agentId, checked, onChange, resumeRef }) => {
  const { rows, resumeAll } = usePausedChats(agentId);
  const count = rows.length;
  useEffect(() => {
    resumeRef.current = count > 0 ? resumeAll : null;
    return () => { resumeRef.current = null; };
  });
  return <ResumePausedOffer count={count} checked={checked} onChange={onChange} />;
};
